import type { Quest } from '@hollowveil/shared';

/**
 * Quêtes pour l'aventure "Le (R)éveil de l'Éducatrice"
 */

// Quête 0: Tutoriel au Sanctuaire
// Les objectifs s'affichent selon la progression du joueur
export const questSanctuary: Quest = {
  id: 'quest_sanctuary_tutorial',
  name: 'Le Sanctuaire',
  description: 'Le hub central de ton aventure. L\'Oracle te guidera.',
  roomId: 'room_sanctuary',
  objectives: [
    // === PHASE 1: Tutoriel initial (avant d'avoir parlé à l'Oracle) ===
    {
      id: 'obj_read_parchment',
      description: 'Lire le parchemin sur la Pierre Ancestrale',
      targetObjectId: 'obj_ancient_stone',
      completed: false,
      hideAfterRiddleSolved: 'riddle_oracle_welcome', // Se cache après avoir parlé à l'Oracle
    },
    {
      id: 'obj_switch_spectral',
      description: 'Passer dans le monde spectral',
      requiredWorld: 'spectral',
      targetObjectId: 'world_switch_spectral',
      completed: false,
      hideAfterRiddleSolved: 'riddle_oracle_welcome',
    },
    {
      id: 'obj_talk_oracle_first',
      description: 'Parler à l\'Oracle Chevelu',
      targetObjectId: 'obj_oracle',
      completed: false,
      hideAfterRiddleSolved: 'riddle_oracle_welcome',
    },
    // === PHASE 2: Aller à Wonderland ===
    {
      id: 'obj_enter_wonderland',
      description: 'Entrer dans le portail de Wonderland',
      targetObjectId: 'obj_portal_wonderland',
      completed: false,
      showAfterRiddleSolved: 'riddle_oracle_welcome',
      hideAfterRoomComplete: 'room_wonderland_reveil',
    },
    // === PHASE 3: Après Wonderland - Direction Hawkins ===
    {
      id: 'obj_talk_oracle_hawkins',
      description: 'Parler à l\'Oracle pour la suite',
      targetObjectId: 'obj_oracle',
      completed: false,
      showAfterRoomComplete: 'room_wonderland_reveil',
      hideAfterRoomComplete: 'room_hawkins_reveil',
    },
    {
      id: 'obj_enter_hawkins',
      description: 'Entrer dans le portail de Hawkins',
      targetObjectId: 'obj_portal_hawkins',
      completed: false,
      showAfterRoomComplete: 'room_wonderland_reveil',
      hideAfterRoomComplete: 'room_hawkins_reveil',
    },
    // === PHASE 4: Après Hawkins - Direction Jurassic ===
    {
      id: 'obj_talk_oracle_jurassic',
      description: 'Parler à l\'Oracle pour la suite',
      targetObjectId: 'obj_oracle',
      completed: false,
      showAfterRoomComplete: 'room_hawkins_reveil',
      hideAfterRoomComplete: 'room_jurassic_reveil',
    },
    {
      id: 'obj_enter_jurassic',
      description: 'Entrer dans le portail de Jurassic Park',
      targetObjectId: 'obj_portal_jurassic',
      completed: false,
      showAfterRoomComplete: 'room_hawkins_reveil',
      hideAfterRoomComplete: 'room_jurassic_reveil',
    },
    // === PHASE 5: Après Jurassic - Direction Winterfell ===
    {
      id: 'obj_talk_oracle_winterfell',
      description: 'Parler à l\'Oracle pour la suite',
      targetObjectId: 'obj_oracle',
      completed: false,
      showAfterRoomComplete: 'room_jurassic_reveil',
      hideAfterRoomComplete: 'room_winterfell_reveil',
    },
    {
      id: 'obj_enter_winterfell',
      description: 'Entrer dans le portail de Winterfell',
      targetObjectId: 'obj_portal_winterfell',
      completed: false,
      showAfterRoomComplete: 'room_jurassic_reveil',
      hideAfterRoomComplete: 'room_winterfell_reveil',
    },
    // === PHASE 6: Après Winterfell - Direction Terre du Milieu ===
    {
      id: 'obj_talk_oracle_middleearth',
      description: 'Parler à l\'Oracle pour la suite',
      targetObjectId: 'obj_oracle',
      completed: false,
      showAfterRoomComplete: 'room_winterfell_reveil',
      hideAfterRoomComplete: 'room_middle_earth_reveil',
    },
    {
      id: 'obj_enter_middleearth',
      description: 'Entrer dans le portail de la Terre du Milieu',
      targetObjectId: 'obj_portal_middleearth',
      completed: false,
      showAfterRoomComplete: 'room_winterfell_reveil',
      hideAfterRoomComplete: 'room_middle_earth_reveil',
    },
    // === PHASE FINALE: Tous les mondes complétés ===
    {
      id: 'obj_talk_oracle_finale',
      description: 'Parler à l\'Oracle pour la révélation finale',
      targetObjectId: 'obj_oracle',
      completed: false,
      showAfterRoomComplete: 'room_middle_earth_reveil',
    },
  ],
  subQuests: [
    {
      id: 'subquest_hub_animals',
      name: 'Amis Célestes',
      description: 'Caresse les créatures du Sanctuaire.',
      icon: '✨',
      isOptional: true,
      isComplete: false,
      objectives: [
        {
          id: 'obj_pet_star',
          description: 'Caresser l\'Étoile Filante',
          targetObjectId: 'npc_shooting_star',
          completed: false,
        },
        {
          id: 'obj_pet_butterfly',
          description: 'Caresser le Papillon Cosmique (monde spectral)',
          requiredWorld: 'spectral',
          targetObjectId: 'npc_cosmic_butterfly',
          completed: false,
        },
        {
          id: 'obj_pet_cloud',
          description: 'Caresser le Nuage Moelleux',
          targetObjectId: 'npc_fluffy_cloud',
          completed: false,
        },
      ],
    },
  ],
  isComplete: false,
  reward: 'Tu as rencontré l\'Oracle et compris ta mission. Le voyage commence !',
};

// Quête 1: Wonderland - Le Chapelier
// Structure en 2 étapes principales avec exploration Real/Spectral
export const questWonderland: Quest = {
  id: 'quest_wonderland',
  name: 'Le Chapelier qui ne riait plus',
  description: 'Aide le Chapelier Fou à surmonter son anxiété sociale et à organiser une tea party.',
  roomId: 'room_wonderland_reveil',
  objectives: [
    // Étape 1: Comprendre le problème (Monde Réel + exploration Spectral)
    {
      id: 'obj_talk_hatter',
      description: 'Parler au Chapelier et comprendre son anxiété',
      targetObjectId: 'obj_hatter',
      completed: false,
    },
    // Étape 2: Placer les éléments sur la table
    {
      id: 'obj_setup_tea_party',
      description: 'Placer les 4 éléments sur les cercles magiques',
      targetObjectId: 'obj_tea_tile_cake', // Se complète quand le dernier item est placé
      completed: false,
      showAfterRiddleSolved: 'hatter_first_meeting', // N'apparaît qu'après avoir parlé au Chapelier
    },
    // Étape 3: Faire valider par le Chapelier (monde réel)
    {
      id: 'obj_tea_party',
      description: 'Parler au Chapelier pour qu\'il valide la tea party',
      targetObjectId: 'obj_hatter', // Se complète quand le Chapelier valide (dialogue hatter_validate)
      completed: false,
      showAfterRiddleSolved: 'tea_placed_cake', // N'apparaît qu'après avoir placé tous les items
    },
    // Étape 4: Retrouver le Chapelier dans le monde spectral
    {
      id: 'obj_hatter_spectral',
      description: 'Retrouver le Chapelier apaisé dans le monde spectral',
      targetObjectId: 'obj_hatter_spectral',
      requiredWorld: 'spectral',
      completed: false,
      showAfterRiddleSolved: 'hatter_validate', // N'apparaît qu'après validation de la tea party
    },
  ],
  subQuests: [
    // Sous-quête optionnelle: retrouver les souvenirs heureux
    {
      id: 'subquest_memories',
      name: 'Souvenirs Heureux',
      description: 'Retrouve les 3 souvenirs qui rappellent au Chapelier qu\'il était capable de belles choses.',
      icon: '📸',
      isOptional: true,
      isComplete: false,
      objectives: [
        {
          id: 'obj_memory_1',
          description: 'Trouver la photo sous le champignon',
          targetObjectId: 'obj_memory_photo_1',
          completed: false,
        },
        {
          id: 'obj_memory_2',
          description: 'Parler au Lièvre de Mars',
          targetObjectId: 'obj_march_hare',
          completed: false,
        },
        {
          id: 'obj_memory_3',
          description: 'Résoudre l\'énigme du Chat du Cheshire',
          targetObjectId: 'obj_cheshire_cat',
          completed: false,
        },
      ],
    },
    // Sous-quête optionnelle: caresser les animaux
    {
      id: 'subquest_wonderland_animals',
      name: 'Amis du Wonderland',
      description: 'Caresse tous les animaux du pays des merveilles.',
      icon: '🐾',
      isOptional: true,
      isComplete: false,
      objectives: [
        {
          id: 'obj_pet_cheshire',
          description: 'Caresser le Chat du Cheshire',
          targetObjectId: 'npc_cheshire',
          completed: false,
        },
        {
          id: 'obj_pet_rabbit',
          description: 'Caresser le Lapin Blanc',
          targetObjectId: 'npc_white_rabbit',
          completed: false,
        },
        {
          id: 'obj_pet_caterpillar',
          description: 'Caresser la Chenille',
          targetObjectId: 'npc_caterpillar',
          completed: false,
        },
        {
          id: 'obj_pet_flamingo',
          description: 'Caresser le Flamant Rose',
          targetObjectId: 'npc_flamingo',
          completed: false,
        },
        {
          id: 'obj_pet_hedgehog',
          description: 'Caresser le Hérisson',
          targetObjectId: 'npc_hedgehog',
          completed: false,
        },
      ],
    },
  ],
  isComplete: false,
  reward: 'Le Chapelier sourit à nouveau ! Tu as appris l\'importance de l\'écoute active et de faire les choses étape par étape.',
};

// Quête 2: Hawkins - Vékeuna
export const questHawkins: Quest = {
  id: 'quest_hawkins',
  name: 'Vékeuna l\'Incompris',
  description: 'Aide Vékeuna à comprendre qu\'il n\'est pas un monstre et à accepter ses émotions.',
  roomId: 'room_hawkins_reveil',
  objectives: [
    {
      id: 'obj_talk_dustin',
      description: 'Parler à Dustin pour comprendre l\'histoire',
      targetObjectId: 'obj_dustin',
      completed: false,
    },
    {
      id: 'obj_help_dustin',
      description: 'Aider Dustin avec les vélos',
      targetObjectId: 'obj_bike_rack',
      showAfterRiddleSolved: 'dustin_first_contact',
      hideAfterRiddleSolved: 'dustin_bike_quest_done',
      completeOnRiddleSolved: 'dustin_bike_quest_done',
      completed: false,
    },
    {
      id: 'obj_talk_erica',
      description: 'Apprendre le Code Secret avec Erica',
      targetObjectId: 'obj_erica',
      showAfterRiddleSolved: 'dustin_bike_quest_done',
      hideAfterRiddleSolved: 'erica_first_contact',
      completed: false,
    },
    {
      id: 'obj_erica_walkie_puzzle',
      description: 'Envoyer le message AMI sur le Walkie-Talkie',
      targetObjectId: 'obj_erica',
      showAfterRiddleSolved: 'erica_first_contact',
      hideAfterRiddleSolved: 'erica_puzzle_done',
      completeOnRiddleSolved: 'erica_puzzle_done',
      completed: false,
    },
    {
      id: 'obj_enter_rift',
      description: 'Entrer dans la Faille Dimensionnelle',
      targetObjectId: 'obj_dimensional_rift',
      showAfterRiddleSolved: 'erica_puzzle_done',
      hideAfterRiddleSolved: 'rift_opened',
      completed: false,
    },
    {
      id: 'obj_confront_vekeuna',
      description: 'Aider Vékeuna à accepter ses émotions',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_vekeuna',
      showAfterRiddleSolved: 'rift_opened',
      completed: false,
    },
  ],
  subQuests: [
    {
      id: 'subquest_dustin_bikes',
      name: 'Aider Dustin',
      description: 'Retrouve les vélos perdus et ramène-les au râtelier.',
      icon: '🚲',
      isOptional: false,
      isComplete: false,
      showAfterRiddleSolved: 'dustin_first_contact',
      hideAfterRiddleSolved: 'dustin_bike_quest_done',
      objectives: [
        {
          id: 'obj_collect_bike_blue',
          description: 'Retrouver le vélo bleu de Lucas',
          targetObjectId: 'obj_bike_lost_1',
          completed: false,
        },
        {
          id: 'obj_collect_bike_yellow',
          description: 'Retrouver le vélo jaune de Mike',
          targetObjectId: 'obj_bike_lost_2',
          completed: false,
        },
        {
          id: 'obj_return_bikes',
          description: 'Ramener les vélos à la maison de Dustin',
          targetObjectId: 'obj_dustin_house',
          completeOnRiddleSolved: 'dustin_bike_quest_done',
          completed: false,
        },
      ],
    },
    {
      id: 'subquest_hawkins_animals',
      name: 'Amis de Hawkins',
      description: 'Caresse les animaux du quartier et de l\'Upside Down.',
      icon: '🐾',
      isOptional: true,
      isComplete: false,
      objectives: [
        {
          id: 'obj_pet_cat',
          description: 'Caresser le Chat de Quartier',
          targetObjectId: 'npc_neighborhood_cat',
          completed: false,
        },
        {
          id: 'obj_pet_dog',
          description: 'Caresser le Chien du Voisin',
          targetObjectId: 'npc_neighbor_dog',
          completed: false,
        },
        {
          id: 'obj_pet_squirrel',
          description: 'Caresser l\'Écureuil',
          targetObjectId: 'npc_squirrel',
          completed: false,
        },
        {
          id: 'obj_pet_demodog',
          description: 'Caresser le Démo-chien (monde spectral)',
          requiredWorld: 'spectral',
          targetObjectId: 'npc_demodog',
          completed: false,
        },
      ],
    },
    // Sous-quête Boss: Demagordon - LaBog Challenge
    {
      id: 'subquest_demagordon_boss',
      name: 'LaBog du Demagordon',
      description: 'Affronte le Demagordon dans un duel de mots ! Prouve ta maîtrise du langage.',
      icon: '🎮',
      isOptional: true,
      isComplete: false,
      showAfterRiddleSolved: 'rift_opened',
      hideAfterRiddleSolved: 'demagordon_defeated',
      objectives: [
        {
          id: 'obj_defeat_demagordon',
          description: 'Vaincre le Demagordon dans un duel de LaBog',
          requiredWorld: 'spectral',
          targetObjectId: 'obj_demagordon',
          completed: false,
          completeOnRiddleSolved: 'demagordon_defeated',
        },
      ],
    },
  ],
  isComplete: false,
  reward: 'Vékeuna contrôle maintenant ses pouvoirs ! Tu as obtenu le Walkie-Talkie de l\'Amitié. Tu as appris : La patience face à la colère.',
};

// Quête 3: Jurassic Park - Blue
export const questJurassic: Quest = {
  id: 'quest_jurassic',
  name: 'Blue et la Gestion de la Colère',
  description: 'Aide Blue le vélociraptor à comprendre que sa colère cache du chagrin.',
  roomId: 'room_jurassic_reveil',
  objectives: [
    {
      id: 'obj_read_file',
      description: 'Lire le dossier de Blue pour trouver le code',
      targetObjectId: 'obj_blue_file',
      completed: false,
    },
    {
      id: 'obj_talk_kind_scientist',
      description: 'Parler au Dr. Sarah (scientifique bienveillante)',
      targetObjectId: 'obj_scientist_kind',
      completed: false,
    },
    {
      id: 'obj_open_cage',
      description: 'Ouvrir la cage de Blue avec le code',
      targetObjectId: 'obj_control_panel',
      completed: false,
    },
    {
      id: 'obj_see_emotions',
      description: 'Observer les vraies émotions de Blue (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_blue_anger_aura',
      completed: false,
    },
    {
      id: 'obj_calm_blue',
      description: 'Calmer Blue avec la désescalade',
      targetObjectId: 'obj_blue',
      completed: false,
    },
  ],
  subQuests: [
    {
      id: 'subquest_jurassic_animals',
      name: 'Amis Préhistoriques',
      description: 'Caresse les dinosaures du parc.',
      icon: '🦕',
      isOptional: true,
      isComplete: false,
      objectives: [
        {
          id: 'obj_pet_baby_raptor',
          description: 'Caresser le Bébé Raptor (ton futur compagnon !)',
          targetObjectId: 'npc_baby_raptor',
          completed: false,
        },
        {
          id: 'obj_pet_brachio',
          description: 'Caresser le Brachiosaure',
          targetObjectId: 'npc_brachiosaurus',
          completed: false,
        },
        {
          id: 'obj_pet_trike',
          description: 'Caresser le Tricératops',
          targetObjectId: 'npc_triceratops',
          completed: false,
        },
        {
          id: 'obj_pet_compy',
          description: 'Caresser le Compy',
          targetObjectId: 'npc_compy',
          completed: false,
        },
      ],
    },
  ],
  isComplete: false,
  reward: 'Blue est en paix. Le bébé raptor te suit maintenant ! Tu as obtenu la Griffe de Résilience. Tu as appris : La désescalade.',
};

// Quête 4: Winterfell - Tyrion
export const questWinterfell: Quest = {
  id: 'quest_winterfell',
  name: 'Tyrion et l\'Estime de Soi',
  description: 'Aide Tyrion à briser les chaînes de ses blessures et à voir sa vraie valeur.',
  roomId: 'room_winterfell_reveil',
  objectives: [
    {
      id: 'obj_talk_tyrion',
      description: 'Parler à Tyrion (il va utiliser l\'humour pour éviter)',
      targetObjectId: 'obj_tyrion',
      completed: false,
    },
    {
      id: 'obj_see_chains',
      description: 'Voir les chaînes de Tyrion (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_chain_monster',
      completed: false,
    },
    {
      id: 'obj_proof_compassion',
      description: 'Trouver la preuve de compassion (témoignage du serviteur)',
      targetObjectId: 'obj_servant_testimony',
      completed: false,
    },
    {
      id: 'obj_proof_accomplishment',
      description: 'Trouver la preuve d\'accomplissement (livre de Tyrion)',
      targetObjectId: 'obj_tyrion_book',
      completed: false,
    },
    {
      id: 'obj_proof_pride',
      description: 'Trouver la preuve de fierté (lettre d\'admiration)',
      targetObjectId: 'obj_admiration_letter',
      completed: false,
    },
    {
      id: 'obj_proof_love',
      description: 'Trouver la preuve d\'amour (dessin de Myrcella)',
      targetObjectId: 'obj_love_token',
      completed: false,
    },
    {
      id: 'obj_proof_strength',
      description: 'Trouver la preuve de force (médaille de bravoure)',
      targetObjectId: 'obj_bravery_medal',
      completed: false,
    },
  ],
  subQuests: [
    {
      id: 'subquest_winterfell_animals',
      name: 'Amis du Nord',
      description: 'Caresse les animaux de Winterfell.',
      icon: '🐺',
      isOptional: true,
      isComplete: false,
      objectives: [
        {
          id: 'obj_pet_ghost',
          description: 'Caresser Ghost le loup-garou',
          targetObjectId: 'npc_ghost',
          completed: false,
        },
        {
          id: 'obj_pet_raven',
          description: 'Caresser le Corbeau',
          targetObjectId: 'npc_raven',
          completed: false,
        },
        {
          id: 'obj_pet_horse',
          description: 'Caresser le Cheval de Guerre',
          targetObjectId: 'npc_horse',
          completed: false,
        },
        {
          id: 'obj_pet_sheepdog',
          description: 'Caresser le Chien de Berger',
          targetObjectId: 'npc_sheepdog',
          completed: false,
        },
      ],
    },
  ],
  isComplete: false,
  reward: 'Tyrion voit enfin sa vraie valeur ! Tu as obtenu l\'Épée de la Persévérance. Tu as appris : L\'adaptation.',
};

// Quête 5: Terre du Milieu - Eärwen
export const questMiddleEarth: Quest = {
  id: 'quest_middle_earth',
  name: 'Eärwen, l\'Elfe qui Apprenait Autrement',
  description: 'Aide Eärwen à prouver que différent ne veut pas dire moins bien.',
  roomId: 'room_middle_earth_reveil',
  objectives: [
    {
      id: 'obj_find_earwen',
      description: 'Trouver Eärwen (cachée dans le jardin)',
      targetObjectId: 'obj_earwen',
      completed: false,
    },
    {
      id: 'obj_discover_art',
      description: 'Découvrir son talent de dessin (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_hidden_art_1',
      completed: false,
    },
    {
      id: 'obj_discover_sculpture',
      description: 'Découvrir son talent de sculpture (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_hidden_sculpture',
      completed: false,
    },
    {
      id: 'obj_discover_dance',
      description: 'Découvrir son talent de danse magique (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_hidden_dance',
      completed: false,
    },
    {
      id: 'obj_get_clay',
      description: 'Récupérer l\'argile pour sculpter',
      targetObjectId: 'obj_clay',
      completed: false,
    },
    {
      id: 'obj_talk_master',
      description: 'Parler au Maître Elrohir',
      targetObjectId: 'obj_elf_master',
      completed: false,
    },
    {
      id: 'obj_alternative_test',
      description: 'Aider Eärwen à réussir le test avec SES méthodes',
      targetObjectId: 'obj_recitation_desk',
      completed: false,
    },
  ],
  subQuests: [
    {
      id: 'subquest_middleearth_animals',
      name: 'Amis de Fondcombe',
      description: 'Caresse les créatures de la Terre du Milieu.',
      icon: '🦅',
      isOptional: true,
      isComplete: false,
      objectives: [
        {
          id: 'obj_pet_pony',
          description: 'Caresser le Poney de la Comté',
          targetObjectId: 'npc_hobbit_pony',
          completed: false,
        },
        {
          id: 'obj_pet_eagle',
          description: 'Caresser le Grand Aigle',
          targetObjectId: 'npc_great_eagle',
          completed: false,
        },
        {
          id: 'obj_pet_rabbit_me',
          description: 'Caresser le Lapin de Fondcombe',
          targetObjectId: 'npc_garden_rabbit',
          completed: false,
        },
        {
          id: 'obj_pet_butterfly_me',
          description: 'Caresser le Papillon Enchanté',
          targetObjectId: 'npc_enchanted_butterfly',
          completed: false,
        },
      ],
    },
  ],
  isComplete: false,
  reward: 'Eärwen a prouvé sa valeur ! Tu as obtenu la Feuille de Nouveau Départ. Tu as appris : L\'accompagnement. Tu es maintenant... LA GRANDE ÉDUCATRICE.',
};

// Quête Finale: La Révélation
export const questFinale: Quest = {
  id: 'quest_finale',
  name: 'La Révélation',
  description: 'L\'Oracle Chevelu veut te révéler quelque chose d\'important...',
  roomId: 'room_finale',
  objectives: [
    {
      id: 'obj_enter_finale',
      description: 'Entrer dans la Salle de la Révélation',
      targetObjectId: 'obj_main_stage',
      completed: false,
    },
    {
      id: 'obj_talk_characters',
      description: 'Parler aux personnages que tu as aidés',
      targetObjectId: 'obj_mad_hatter_finale',
      completed: false,
    },
    {
      id: 'obj_approach_oracle',
      description: 'S\'approcher de l\'Oracle pour la grande révélation',
      targetObjectId: 'obj_oracle_finale',
      completed: false,
    },
    {
      id: 'obj_see_truth',
      description: 'Voir le message caché en monde spectral',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_love_message',
      completed: false,
    },
  ],
  subQuests: [
    {
      id: 'subquest_finale_animals',
      name: 'Célébration Finale',
      description: 'Caresse les dernières créatures de la fête.',
      icon: '🎉',
      isOptional: true,
      isComplete: false,
      objectives: [
        {
          id: 'obj_pet_golden_butterfly',
          description: 'Caresser le Papillon Doré',
          targetObjectId: 'npc_golden_butterfly',
          completed: false,
        },
        {
          id: 'obj_pet_dove',
          description: 'Caresser la Colombe',
          targetObjectId: 'npc_dove',
          completed: false,
        },
        {
          id: 'obj_pet_mini_phoenix',
          description: 'Caresser le Petit Phoenix (monde spectral)',
          requiredWorld: 'spectral',
          targetObjectId: 'npc_mini_phoenix',
          completed: false,
        },
      ],
    },
  ],
  isComplete: false,
  reward: 'Tu es la Grande Éducatrice ! Benours t\'aime de tout son coeur chauve. Félicitations, tu as terminé Le (R)éveil de l\'Éducatrice !',
};

// Export de toutes les quêtes
export const reveilQuests: Quest[] = [
  questSanctuary,
  questWonderland,
  questHawkins,
  questJurassic,
  questWinterfell,
  questMiddleEarth,
  questFinale,
];
